import {
  LevelLocation,
  STEP_LOCATION,
  TYPE_REDUCER,
  ThreeLevelLocation,
  useServices,
  useTenantConfig,
  useThreeLevelLocationContext,
} from 'contexts';
import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import { t } from 'i18n';
import {
  DISTRICT,
  PROVINCE,
  UPDATE_VALUE_DISTRICT,
  UPDATE_VALUE_PROVINCE,
  UPDATE_VALUE_WARD,
  WARD,
} from 'constants/threeLevelLocation';
import { Area, AreaLayer, SearchArea } from '@teko/location';

const filterLocationsV2 = (searchAreas: SearchArea[], limits: string[]) => {
  const areas = searchAreas.map(
    ({ id, code, title }) => ({ id, code, title } as Area),
  );
  return limits && limits.length > 0
    ? areas.filter((area) =>
        limits.some((allowDis) => {
          const re = new RegExp(`^${allowDis}$`);
          return re.test(area.code);
        }),
      )
    : areas;
};

export const useLevelThreeLevelLocation = ({
  threeLevelLocation,
  stepLocation,
  updateThreeLevelLocation,
  setStepLocation,
  onClosePopup,
  onSubmitNewLocation,
}: {
  threeLevelLocation: ThreeLevelLocation;
  updateThreeLevelLocation?: (action: {
    type: TYPE_REDUCER;
    value: LevelLocation;
  }) => void;
  stepLocation?: STEP_LOCATION;
  setStepLocation?: (stepLocation: STEP_LOCATION) => void;
  onClosePopup: () => void;
  onSubmitNewLocation?: (
    newThreeLevelLocation: ThreeLevelLocation,
  ) => Promise<void>;
}) => {
  const { locationService, locationServiceV3 } = useServices();
  const { epicOmni1589 } = useTenantConfig();
  const [keyword, setKeyword] = useState('');
  const { showThreeLevelLocationPicker } = useThreeLevelLocationContext();

  const { data: provinces, isValidating: isLoadingProvince } = useSWR(
    !epicOmni1589
      ? ['/provinces', stepLocation, showThreeLevelLocationPicker]
      : null,
    async () => {
      const provinceResponse = await locationService.getProvinces();
      return provinceResponse.map((item) => ({
        value: item.code,
        label: item.name,
        id: item.id,
      }));
    },
    {
      fallbackData: [],
      revalidateOnFocus: false,
      isPaused: () => stepLocation !== PROVINCE,
    },
  );

  const { data: districts, isValidating: isLoadingDistricts } = useSWR(
    !epicOmni1589
      ? [
          '/districts',
          threeLevelLocation.province.code,
          showThreeLevelLocationPicker,
          stepLocation,
        ]
      : null,
    async () => {
      const districtResponse = await locationService.getDistrictsByProvinceCode(
        threeLevelLocation.province.code,
      );
      return districtResponse.map((item) => ({
        value: item.code,
        label: item.name,
        id: item.id,
      }));
    },
    {
      fallbackData: [],
      revalidateOnFocus: false,
      isPaused: () =>
        !threeLevelLocation.province.code ||
        !showThreeLevelLocationPicker ||
        stepLocation !== DISTRICT,
    },
  );

  const { data: wards, isValidating: isLoadingWards } = useSWR(
    !epicOmni1589
      ? [
          '/wards',
          threeLevelLocation.district?.code,
          showThreeLevelLocationPicker,
          stepLocation,
        ]
      : null,
    async () => {
      const wardResponse = !!threeLevelLocation.district
        ? await locationService.getWardsByDistrictCode(
            threeLevelLocation.district.code,
          )
        : [];
      return wardResponse.map((item) => ({
        value: item.code,
        label: item.name,
        id: item.id,
      }));
    },
    {
      fallbackData: [],
      isPaused: () =>
        !threeLevelLocation.district?.code ||
        !showThreeLevelLocationPicker ||
        stepLocation !== WARD,
      revalidateOnFocus: false,
    },
  );

  const { data: provincesV2, isValidating: isLoadingProvincesV2 } = useSWR(
    epicOmni1589
      ? ['provinces', stepLocation, showThreeLevelLocationPicker]
      : null,
    async () => {
      const response = await locationServiceV3.getAreas({
        areaLayer: AreaLayer.province,
      });
      return filterLocationsV2(response || [], []).map((item) => ({
        value: item.code,
        label: item.title,
        id: item.id,
      }));
    },
    {
      revalidateOnFocus: false,
      fallbackData: [],
      shouldRetryOnError: false,
      isPaused: () => stepLocation !== PROVINCE,
    },
  );

  const { data: wardsV2, isValidating: isLoadingWardsV2 } = useSWR(
    epicOmni1589
      ? [
          'wards',
          threeLevelLocation.province.id,
          stepLocation,
          showThreeLevelLocationPicker,
        ]
      : null,
    async () => {
      const response = await locationServiceV3.getAreas({
        areaLayer: AreaLayer.ward,
        parentAreaId: threeLevelLocation.province.id,
      });
      return filterLocationsV2(response || [], []).map((item) => ({
        value: item.code,
        label: item.title,
        id: item.id,
      }));
    },
    {
      revalidateOnFocus: false,
      fallbackData: [],
      shouldRetryOnError: false,
      isPaused: () =>
        !threeLevelLocation.province.code ||
        !showThreeLevelLocationPicker ||
        stepLocation !== WARD,
    },
  );

  const originalData = useCallback(() => {
    if (stepLocation === PROVINCE)
      return epicOmni1589 ? provincesV2 : provinces;

    if (stepLocation === DISTRICT && !epicOmni1589) return districts;

    return epicOmni1589 ? wardsV2 : wards;
  }, [
    stepLocation,
    provinces,
    districts,
    wards,
    provincesV2,
    wardsV2,
    epicOmni1589,
  ]);

  const filterLocations = useMemo(() => {
    const data = originalData();
    if (!keyword || keyword === '') return data;
    return (
      data?.filter((option) => {
        return option.label.toLowerCase().includes(keyword.toLowerCase());
      }) || []
    );
  }, [originalData, keyword]);

  const onSelectLocation = (location: LevelLocation) => {
    if (stepLocation === PROVINCE) {
      updateThreeLevelLocation?.({
        type: UPDATE_VALUE_PROVINCE,
        value: location,
      });
      // reset district when change province
      updateThreeLevelLocation?.({
        type: UPDATE_VALUE_DISTRICT,
        value: { id: 0, code: '', label: '' },
      });
      setStepLocation?.(epicOmni1589 ? WARD : DISTRICT);
      return;
    }
    if (!epicOmni1589 && stepLocation === DISTRICT) {
      updateThreeLevelLocation?.({
        type: UPDATE_VALUE_DISTRICT,
        value: location,
      });
      setStepLocation?.(WARD);
      return;
    }
    updateThreeLevelLocation?.({
      type: UPDATE_VALUE_WARD,
      value: location,
    });
    onSubmitNewLocation?.({
      province: threeLevelLocation.province,
      district: threeLevelLocation.district,
      ward: location,
    });
  };

  const backToPreviousLevelLocation = () => {
    if (stepLocation === PROVINCE) {
      onClosePopup();
      return;
    }
    if (stepLocation === DISTRICT) {
      setStepLocation?.(PROVINCE);
      return;
    }
    setStepLocation?.(epicOmni1589 ? PROVINCE : DISTRICT);
  };

  const headerText = useMemo(() => {
    if (stepLocation === PROVINCE) {
      return {
        title: t('Chọn khu vực giao hàng'),
        sutTitle: t('Để biết chính xác tồn kho và thời gian giao hàng'),
        placeHolderSearch: t('Tìm nhanh Tỉnh/ Thành phố'),
      };
    }
    if (stepLocation === DISTRICT && !epicOmni1589) {
      return {
        title: t('Chọn Quận, huyện'),
        sutTitle: `${t('tại')} ${threeLevelLocation.province.label}`,
        placeHolderSearch: t('Tìm nhanh Quận/huyện'),
      };
    }
    return {
      title: t('Chọn Phường, Xã'),
      sutTitle: `${t('tại')}  ${
        !epicOmni1589 ? threeLevelLocation.district?.label + ', ' : ''
      }${threeLevelLocation.province.label}`,
      placeHolderSearch: t('Tìm nhanh Phường/xã'),
    };
  }, [threeLevelLocation, stepLocation, epicOmni1589]);

  return {
    loadingLocation:
      isLoadingProvince ||
      isLoadingDistricts ||
      isLoadingWards ||
      isLoadingProvincesV2 ||
      isLoadingWardsV2,
    filterLocations,
    onSelectLocation,
    backToPreviousLevelLocation,
    setKeyword,
    keyword,
    headerText,
  };
};
